import { useState, type FormEvent, type JSX } from 'react'
import { RotateCcw, X } from 'lucide-react'

const R = 'rounded-[3px]'
const BTN_PRIMARY = `flex h-9 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`
const BTN_SECONDARY = `flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`
const INPUT = `w-full ${R} border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-700 outline-none transition-colors focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/10`

interface MfaResetSelfDialogProps {
  onReset: (password: string) => void
  onCancel: () => void
  loading: boolean
  error: string | null
}

export function MfaResetSelfDialog({
  onReset,
  onCancel,
  loading,
  error,
}: MfaResetSelfDialogProps): JSX.Element {
  const [password, setPassword] = useState('')

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    if (password) {
      onReset(password)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Reset MFA"
    >
      <div className={`w-full max-w-md border border-slate-200 bg-white p-6 shadow-xl ${R}`}>
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}
            >
              <RotateCcw className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
                Reset Multi-Factor Authentication
              </h3>
              <p className="mt-0.5 text-[12.5px] text-slate-500">
                Reset your authenticator key and generate fresh backup codes.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`${R} p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-4 text-[13px] leading-relaxed text-slate-600">
          Enter your current password to reset your authenticator app. You will immediately be guided through setting up your new authenticator and saving your new backup codes.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="mfa-reset-self-password"
              className="mb-1 block text-xs font-semibold text-slate-700"
            >
              Current password
            </label>
            <input
              id="mfa-reset-self-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT}
              placeholder="Enter your current password"
              autoFocus
              required
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className={BTN_SECONDARY}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password}
              className={BTN_PRIMARY}
            >
              {loading ? 'Resetting…' : 'Reset & Reconfigure MFA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
